import { faDownload, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

interface EditableViewerProps {
  title?: string;
  text: string;
  isExpandable?: boolean;
  allowUpload?: boolean;
  allowDownload?: boolean;
  customUploadFunction?: any;
}

function EditableViewer({
  title,
  text,
  isExpandable,
  allowUpload,
  allowDownload,
  customUploadFunction,
}: EditableViewerProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const toggleCollapse = () => {
    setIsCollapsed(isExpandable ? !isCollapsed : false);
  };

  const downloadJson = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (editorRef.current) {
      const content = editorRef.current?.innerText;
      const looksLikeJsonLd =
        content.includes('"@context"') || content.includes('"@type"');
      const blob = new Blob([content], {
        type: looksLikeJsonLd ? "application/ld+json" : "application/json",
      });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = looksLikeJsonLd ? "metadata.jsonld" : "meta.json";

      a.click();

      URL.revokeObjectURL(a.href);
    }
  };

  const uploadJson = (event: React.MouseEvent) => {
    event.stopPropagation();

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json";

    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement)?.files?.[0];

      if (file) {
        console.log("Uploaded");
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const jsonContent = JSON.parse(e.target?.result as string);
            if (editorRef.current) {
              editorRef.current.querySelector("pre")!.innerHTML =
                JSON.stringify(jsonContent, null, 2);
              editorRef.current.style.height = `${editorRef.current.scrollHeight}px`;
            }
          } catch (_error) {
            alert("Invalid JSON file.");
          }
        };

        reader.readAsText(file);
      }
    };

    input.click();
  };

  useEffect(() => {
    if (editorRef.current) {
      if (!isCollapsed) {
        editorRef.current.style.height = `${editorRef.current.scrollHeight}px`;
      } else {
        editorRef.current.style.height = "0";
      }
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (editorRef.current) {
      if (!isCollapsed) {
        editorRef.current.style.height = `${editorRef.current.scrollHeight}px`;
      } else {
        editorRef.current.style.height = "0";
      }
    }
  }, [isCollapsed]);

  return (
    <EditorWrapper
      disabled={text === "{}"}
      $isCollapsed={isCollapsed}
      $isExpandable={isExpandable}
    >
      <h1
        onClick={toggleCollapse}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggleCollapse();
        }}
      >
        <span>{title ?? "JSON Editor"}</span>
        {allowUpload && (
          <FontAwesomeIcon
            icon={faUpload}
            onClick={customUploadFunction ?? uploadJson}
          />
        )}
        {allowDownload && (
          <FontAwesomeIcon icon={faDownload} onClick={downloadJson} />
        )}
      </h1>
      <Editor ref={editorRef} contentEditable suppressContentEditableWarning>
        <pre>{text}</pre>
      </Editor>
    </EditorWrapper>
  );
}

const EditorWrapper = styled.div<{
  $isCollapsed: boolean;
  $isExpandable?: boolean;
  disabled?: boolean;
}>`
    overflow-y: auto;
    counter-reset: line;
    font-size: var(--size-smaller);
    border-radius: 12px;
    overflow: hidden;

    background-color: var(--color-dark-green);
    background: linear-gradient(90deg, var(--color-dark-green), var(--color-primary-desat) 150%);
    color: var(--color-primary-light);

    width: 100%;
    align-self: flex-start;

    pointer-events: ${({ disabled }) => (disabled ? "none" : "all")};

    filter: ${({ disabled }) => (disabled ? "grayscale(1)" : "unset")};

    h1 {
        font-size: var(--size-main);
        font-weight: 700;
        color: rgb(var(--color-dashboard-bg));
        cursor: ${({ $isExpandable }) => ($isExpandable ? "pointer" : "")};
        margin: 0;
        width: calc(100% - 32px - 4px);
        transition: all .2s;
        padding: 16px;
        border: 2px solid transparent;
        border-radius: ${({ $isCollapsed }) => ($isCollapsed ? "12px" : "12px 12px 0 0")};
        transition: all .2s;
        display: flex;
        justify-content: space-between;

        span::after
        {
            position: absolute;
            content:  ${({ $isExpandable }) => ($isExpandable ? "▼" : "")};;
            margin-left: 8px;
            transform: scaleY(.6) scaleX(1.1) scaleY(${({ $isCollapsed }) => ($isCollapsed ? "1" : "-1")});
            transition: all .2s;
        }

        &:hover
        {
            border-color: ${({ $isExpandable }) => ($isExpandable ? "#6aa98b" : "transparent")}
        }

        svg
        {
            font-size: .9em;
            cursor: pointer;
        }
    }

    div {
        padding: 0 16px;
        border-bottom-width: ${({ $isCollapsed }) => ($isCollapsed ? "0px" : "2px")};
        border-radius: 0 0 12px 12px;
    }
`;

const Editor = styled.div`
    margin: 0;
    border: 0;
    height: 0;
    overflow: hidden;
    transition: height 0.3s ease, padding 0.3s ease;
    width: calc(100% - 32px -4px);
    border: 2px solid var(--color-dark-green);
    border-top-width: 0;
    background-color: rgb(var(--color-dashboard-bg));
    color: var(--color-dark-green);

    pre {
        white-space: pre-wrap;
        word-wrap: break-word;
        position: relative;
    }
`;

export default EditableViewer;
